import { useSyncExternalStore } from 'react';
import { AppError, toAppError, type AnswerEvent, type AnswerEventResult } from '@mt/contracts';
import { rpc } from './api.ts';
import { localStore } from './platform.ts';
import { reportError } from './telemetry.ts';

/**
 * Durable, per-account queue of answer/hint/reveal events.
 *
 * - Events are persisted locally (keyed by user id) before sending, so a brief
 *   disconnect or reload never loses an answer.
 * - Events are sent one at a time, in order. Each has a client-generated id; the
 *   server treats a repeated id as a duplicate, so retries are safe.
 * - Network/server failures retry with exponential backoff; permanent
 *   rejections (e.g. session expired) are dropped and reported to listeners.
 * - The queue is cleared on sign-out and partitioned per account.
 */
export type SyncState = 'idle' | 'saving' | 'retrying' | 'offline' | 'paused';

export interface QueueSnapshot {
  state: SyncState;
  pending: number;
  pendingBySession: Record<string, number>;
  lastError: AppError | null;
}

type ResultListener = (event: AnswerEvent, result: AnswerEventResult) => void;
type RejectListener = (event: AnswerEvent, error: AppError) => void;

const RETRYABLE = new Set(['NETWORK_ERROR', 'BACKEND_UNAVAILABLE', 'UNKNOWN']);
const MAX_BACKOFF_MS = 30_000;

class AnswerQueue {
  private userId: string | null = null;
  private events: AnswerEvent[] = [];
  private sending = false;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<() => void>();
  private resultListeners = new Set<ResultListener>();
  private rejectListeners = new Set<RejectListener>();
  private snapshot: QueueSnapshot = { state: 'idle', pending: 0, pendingBySession: {}, lastError: null };
  private state: SyncState = 'idle';
  private lastError: AppError | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.kick(true));
      window.addEventListener('offline', () => this.update());
    }
  }

  private key(): string | null {
    return this.userId ? `mt:queue:${this.userId}` : null;
  }

  private persist(): void {
    const key = this.key();
    if (!key) return;
    if (this.events.length === 0) localStore.removeItem(key);
    else localStore.setItem(key, JSON.stringify(this.events));
  }

  /** Switch accounts: loads that account's pending events (never another account's). */
  setUser(userId: string | null): void {
    if (userId === this.userId) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.userId = userId;
    this.events = [];
    this.attempt = 0;
    this.lastError = null;
    const key = this.key();
    if (key) {
      try {
        const raw = localStore.getItem(key);
        this.events = raw ? (JSON.parse(raw) as AnswerEvent[]) : [];
      } catch {
        this.events = [];
      }
    }
    this.update();
    this.kick();
  }

  /** Remove this account's local queue (sign-out). */
  clear(): void {
    const key = this.key();
    if (key) localStore.removeItem(key);
    this.events = [];
    this.update();
  }

  enqueue(event: AnswerEvent): void {
    if (!this.userId) throw new AppError('NOT_AUTHENTICATED', 401);
    this.events.push(event);
    this.persist();
    this.update();
    this.kick();
  }

  pendingFor(sessionId: string): AnswerEvent[] {
    return this.events.filter((e) => e.sessionId === sessionId);
  }

  get pendingCount(): number {
    return this.events.length;
  }

  onResult(listener: ResultListener): () => void {
    this.resultListeners.add(listener);
    return () => this.resultListeners.delete(listener);
  }

  onReject(listener: RejectListener): () => void {
    this.rejectListeners.add(listener);
    return () => this.rejectListeners.delete(listener);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): QueueSnapshot => this.snapshot;

  /** Resolve once every event for the session is confirmed (or reject on a permanent error). */
  waitForSession(sessionId: string, timeoutMs = 60_000): Promise<void> {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      const check = () => {
        if (this.pendingFor(sessionId).length === 0) {
          cleanup();
          resolve();
        } else if (Date.now() - started > timeoutMs) {
          cleanup();
          reject(this.lastError ?? new AppError('NETWORK_ERROR', 0));
        }
      };
      const offReject = this.onReject((event, error) => {
        if (event.sessionId === sessionId && !RETRYABLE.has(error.code)) {
          cleanup();
          reject(error);
        }
      });
      const unsubscribe = this.subscribe(check);
      const interval = setInterval(check, 1000);
      const cleanup = () => {
        offReject();
        unsubscribe();
        clearInterval(interval);
      };
      check();
      this.kick(true);
    });
  }

  /** Try to send now. `force` skips the current backoff wait. */
  kick(force = false): void {
    if (force && this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
      this.attempt = 0;
    }
    if (this.timer) return;
    void this.drain();
  }

  private online(): boolean {
    return typeof navigator === 'undefined' || navigator.onLine !== false;
  }

  private async drain(): Promise<void> {
    if (this.sending || !this.userId) return;
    this.sending = true;
    try {
      while (this.events.length > 0) {
        if (!this.online()) {
          this.update();
          return;
        }
        const event = this.events[0]!;
        this.update('saving');
        try {
          const result = await rpc('submit_answer_event', {
            p_event_id: event.id,
            p_session_id: event.sessionId,
            p_question_id: event.questionId,
            p_kind: event.kind,
            p_answer: event.answer,
            p_hint_level: event.hintLevel,
            p_response_ms: event.responseMs,
            p_client_created_at: event.clientCreatedAt,
          });
          this.events.shift();
          this.persist();
          this.attempt = 0;
          this.lastError = null;
          for (const listener of this.resultListeners) listener(event, result);
        } catch (error) {
          const appError = toAppError(error);
          this.lastError = appError;
          if (RETRYABLE.has(appError.code) || appError.code === 'NOT_AUTHENTICATED') {
            for (const listener of this.rejectListeners) listener(event, appError);
            this.scheduleRetry(appError.code === 'NOT_AUTHENTICATED');
            return;
          }
          // Permanent rejection: drop the event so the queue can continue.
          this.events.shift();
          this.persist();
          // A duplicate answer to a solved question changes nothing; anything else is a real problem.
          reportError('answer_event_rejected', { code: appError.code }, undefined, appError.code === 'QUESTION_ALREADY_ANSWERED' ? 'warning' : 'error');
          for (const listener of this.rejectListeners) listener(event, appError);
        }
      }
      this.update('idle');
    } finally {
      this.sending = false;
    }
  }

  private scheduleRetry(paused: boolean): void {
    this.attempt += 1;
    const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(this.attempt - 1, 5)) * (0.8 + Math.random() * 0.4);
    this.update(paused ? 'paused' : 'retrying');
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.drain();
    }, delay);
  }

  private update(state?: SyncState): void {
    if (state) this.state = state;
    if (this.events.length === 0 && this.state !== 'saving') this.state = 'idle';
    const effective: SyncState = this.events.length > 0 && !this.online() ? 'offline' : this.state;
    const pendingBySession: Record<string, number> = {};
    for (const e of this.events) pendingBySession[e.sessionId] = (pendingBySession[e.sessionId] ?? 0) + 1;
    this.snapshot = { state: effective, pending: this.events.length, pendingBySession, lastError: this.lastError };
    for (const listener of this.listeners) listener();
  }
}

export const answerQueue = new AnswerQueue();

export function useQueueStatus(): QueueSnapshot {
  return useSyncExternalStore(answerQueue.subscribe, answerQueue.getSnapshot, answerQueue.getSnapshot);
}
